import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";

import { useCreateSmartTable } from "@/queries/smart-tables";
import { ToastType } from "@/enums";
import { displayToast } from "@/utils/toast";
import { routePaths } from "@/utils/routePaths";

const useCreateDefaultSmartTable = (folderId?: number, openInNewTab = false) => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const createSmartTableMutation = useCreateSmartTable();
  const [isCreating, setIsCreating] = useState(false);

  const create = async () => {
    if (!workspaceId) {
      return;
    }

    setIsCreating(true);

    try {
      await new Promise<void>((resolve, reject) => {
        createSmartTableMutation.mutate(
          {
            folder_id: folderId ?? null,
          },
          {
            onSuccess: response => {
              if (!response?.id) {
                reject(new Error("Invalid response from server"));
                return;
              }

              displayToast("Table created successfully", ToastType.success);

              const url = routePaths.workspaceSmartTable(workspaceId, response.id);

              if (openInNewTab) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
              resolve();
            },
            onError: error => {
              const getErrorMessage = (err: unknown): string => {
                if (typeof err === "string") {
                  return err;
                }
                if (err instanceof Error) {
                  return err.message;
                }
                return "Error creating table";
              };

              displayToast(getErrorMessage(error), ToastType.error);
              reject(error);
            },
          },
        );
      });
    } finally {
      setIsCreating(false);
    }
  };

  return {
    create,
    isCreating: isCreating || createSmartTableMutation.isPending,
    isError: createSmartTableMutation.isError,
    error: createSmartTableMutation.error,
  };
};

export default useCreateDefaultSmartTable;
